import { useState, useEffect } from "react";
import { School, CheckCircle, XCircle, Users, RefreshCw, GraduationCap, Pencil, X, Loader2, Mail, Building2, Radio } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";

const API = "/api";

export function SchoolsTab({ 
  applications, 
  onApproveSchool, 
  onRejectSchool,
  isMasterAdmin = false
}) {
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, school: null });
  const [editForm, setEditForm] = useState({ school_group: "", school_name: "", invoices_email: "" });
  const [saving, setSaving] = useState(false);
  
  const pendingApplications = applications.filter(s => s.status === "pending");

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const response = await fetch(`${API}/admin/schools`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSchools(data.schools || []);
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
    } finally {
      setLoadingSchools(false);
    }
  };

  const openEditModal = (school) => {
    setEditForm({
      school_group: school.school_group || "",
      school_name: school.school_name || "",
      invoices_email: school.invoices_email || "",
      max_live_participants: school.max_live_participants ?? 3,
      student_limit: school.student_limit ?? 10
    });
    setEditModal({ open: true, school });
  };

  const handleSaveSchool = async () => {
    if (!editForm.school_name.trim()) {
      toast.error("School name cannot be empty");
      return;
    }
    const maxLive = parseInt(editForm.max_live_participants, 10);
    if (isNaN(maxLive) || maxLive < 1 || maxLive > 500) {
      toast.error("Max live players must be between 1 and 500");
      return;
    }
    const maxStudents = parseInt(editForm.student_limit, 10);
    if (isNaN(maxStudents) || maxStudents < 1 || maxStudents > 10000) {
      toast.error("Max students must be between 1 and 10000");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/schools/${editModal.school.school_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...editForm, max_live_participants: maxLive, student_limit: maxStudents })
      });

      if (response.ok) {
        toast.success("School updated successfully");
        setEditModal({ open: false, school: null });
        fetchSchools();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update school");
      }
    } catch (error) {
      toast.error("Error updating school");
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="space-y-8">
      {/* School Applications Section */}
      <div className="bg-card rounded-2xl border border-border/40 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-blue-500/10">
            <School className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg">School Applications</h2>
            <p className="text-sm text-muted-foreground">
              Review and approve school bulk license applications
            </p>
          </div>
        </div>

        {pendingApplications.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <School className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No pending school applications</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingApplications.map((app) => (
              <div
                key={app.application_id}
                className="border border-border rounded-xl p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{app.school_name}</h3>
                    {app.school_website && (
                      <a 
                        href={app.school_website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {app.school_website}
                      </a>
                    )}
                    
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Contact:</p>
                        <p className="font-medium">{app.contact_name}</p>
                        <p className="text-sm">{app.contact_role}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Contact Details:</p>
                        <p className="text-sm">{app.contact_email}</p>
                        {app.contact_phone && <p className="text-sm">{app.contact_phone}</p>}
                      </div>
                    </div>

                    <div className="mt-4">
                      <Badge variant="secondary">
                        ~{app.estimated_students} students
                      </Badge>
                    </div>

                    {app.message && (
                      <p className="mt-4 text-sm text-muted-foreground italic">
                        "{app.message}"
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 text-green-600 hover:bg-green-50"
                      onClick={() => onApproveSchool(app.application_id)}
                      data-testid={`approve-school-${app.application_id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 text-red-600 hover:bg-red-50"
                      onClick={() => onRejectSchool(app.application_id)}
                      data-testid={`reject-school-${app.application_id}`}
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current Schools Section */}
      <div className="bg-card rounded-2xl border border-border/40 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/10">
              <GraduationCap className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg">Current Schools</h2>
              <p className="text-sm text-muted-foreground">
                {schools.length} active school{schools.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSchools}
            disabled={loadingSchools}
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loadingSchools ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {loadingSchools ? (
          <div className="text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
          </div>
        ) : schools.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No schools registered yet</p>
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-[140px]">School Group</TableHead>
                  <TableHead>School Name</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Admin Email</TableHead>
                  <TableHead>Invoices Email</TableHead>
                  <TableHead className="text-right">Students</TableHead>
                  <TableHead className="text-right">Live Players</TableHead>
                  {isMasterAdmin && <TableHead className="w-[80px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {schools.map((school) => (
                  <TableRow key={school.school_id}>
                    <TableCell>
                      {school.school_group ? (
                        <span className="text-sm">{school.school_group}</span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {school.school_name}
                    </TableCell>
                    <TableCell>
                      {school.admin_nickname ? (
                        <span className="text-foreground">{school.admin_nickname}</span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {school.admin_email ? (
                        <span className="text-sm text-muted-foreground">{school.admin_email}</span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {school.invoices_email ? (
                        <span className="text-sm text-muted-foreground">{school.invoices_email}</span>
                      ) : (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5" data-testid={`school-students-${school.school_id}`}>
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span className={`font-medium ${school.student_count >= school.student_limit ? "text-amber-600 dark:text-amber-400" : ""}`}>
                          {school.student_count}
                          <span className="text-muted-foreground font-normal">/{school.student_limit}</span>
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5" data-testid={`school-max-live-${school.school_id}`}>
                        <Radio className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{school.max_live_participants ?? 3}</span>
                      </div>
                    </TableCell>
                    {isMasterAdmin && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(school)}
                          className="h-8 w-8 p-0"
                          data-testid={`edit-school-${school.school_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit School Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, school: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Edit School
            </DialogTitle>
            <DialogDescription>
              Update school details. Admin email cannot be changed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="school_group">School Group</Label>
              <Input
                id="school_group"
                placeholder="e.g., Catholic Schools NSW"
                value={editForm.school_group}
                onChange={(e) => setEditForm(prev => ({ ...prev, school_group: e.target.value }))}
                data-testid="edit-school-group-input"
              />
              <p className="text-xs text-muted-foreground">Optional grouping for schools in the same district or network</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="school_name">School Name</Label>
              <Input
                id="school_name"
                placeholder="School name"
                value={editForm.school_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, school_name: e.target.value }))}
                data-testid="edit-school-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin_email">Admin Email</Label>
              <Input
                id="admin_email"
                value={editModal.school?.admin_email || ""}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">Admin email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoices_email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Invoices Email
              </Label>
              <Input
                id="invoices_email"
                type="email"
                placeholder="invoices@school.edu"
                value={editForm.invoices_email}
                onChange={(e) => setEditForm(prev => ({ ...prev, invoices_email: e.target.value }))}
                data-testid="edit-invoices-email-input"
              />
              <p className="text-xs text-muted-foreground">Email address for sending monthly invoices</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="student_limit" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Max Students
              </Label>
              <Input
                id="student_limit"
                type="number"
                min={1}
                max={10000}
                value={editForm.student_limit}
                onChange={(e) => setEditForm(prev => ({ ...prev, student_limit: e.target.value }))}
                data-testid="edit-max-students-input"
              />
              <p className="text-xs text-muted-foreground">
                Student slot cap for this school (tier defaults: Starter 10, Plus 50, Pro 200). Manual edits override the tier default.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_live_participants" className="flex items-center gap-2">
                <Radio className="w-4 h-4" />
                Max Live Players
              </Label>
              <Input
                id="max_live_participants"
                type="number"
                min={1}
                max={500}
                value={editForm.max_live_participants}
                onChange={(e) => setEditForm(prev => ({ ...prev, max_live_participants: e.target.value }))}
                data-testid="edit-max-live-input"
              />
              <p className="text-xs text-muted-foreground">Max concurrent players allowed in this school's Live Drill sessions (applies to new sessions)</p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setEditModal({ open: false, school: null })}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSchool}
              disabled={saving || !editForm.school_name.trim()}
              data-testid="save-school-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
